import { firstNonEmptyString } from '../../Utils/stringUtils';
import type { FilterOption } from './PriceSimulation.types';
import type { PackageSimulationDto } from './priceSimulationModel';

export interface SkuInstance {
    packageId: string;
    packageName: string;
    tierId: string;
    tierName: string;
    productName: string;
}

export interface SkuFilterSelection {
    packages: FilterOption[];
    tiers: FilterOption[];
    products: FilterOption[];
}

export const buildSkuInstancesMap = (packageDetailsMap: Record<string, PackageSimulationDto>): Record<string, SkuInstance[]> => {
    const map: Record<string, SkuInstance[]> = {};
    Object.entries(packageDetailsMap).forEach(([packageId, details]) => {
        (details.tiers ?? []).forEach((tier) => {
            const { tierId } = tier;
            if (tierId === undefined) return;
            const tierName = tier.name ?? `Tier ${tier.tierId}`;
            const seenSkus = new Set<string>();
            (tier.productGroups ?? []).forEach((group) => {
                (group.products ?? []).forEach((product) => {
                    const sku = firstNonEmptyString(product.articleNumber, product.productId);
                    if (sku === '' || seenSkus.has(sku)) return;
                    seenSkus.add(sku);
                    (map[sku] ??= []).push({
                        packageId,
                        packageName: details.packageName ?? 'Package',
                        tierId,
                        tierName,
                        productName: firstNonEmptyString(product.name, sku),
                    });
                });
            });
        });
    });
    return map;
};

export const countDistinctPackages = (instances: SkuInstance[]): number => new Set(instances.map((instance) => instance.packageId)).size;

export const computeSkuFilterSelection = (sku: string, instances: SkuInstance[], currentPackages: FilterOption[]): SkuFilterSelection => {
    const packages = [...currentPackages];
    const tiers = new Map<string, FilterOption>();
    instances.forEach((instance) => {
        if (!packages.some((pkg) => String(pkg.id) === instance.packageId)) packages.push({ id: instance.packageId, name: instance.packageName });
        if (!tiers.has(instance.tierId)) tiers.set(instance.tierId, { id: instance.tierId, name: instance.tierName });
    });
    return {
        packages,
        tiers: Array.from(tiers.values()),
        products: [{ id: sku, name: instances[0]?.productName ?? sku }],
    };
};
